import type { SpecialtyOption, GroupKey } from './types'
import { GROUP_ORDER, groupLabel } from './groups'

export function specialtyLabel(s: SpecialtyOption, language: string): string {
  return language === 'uk' ? s.nameUk : s.nameEn
}

const norm = (s: string) => s.toLocaleLowerCase('uk').trim()
const words = (s: string) => norm(s).split(/[^\p{L}\p{N}']+/u).filter(Boolean)

function commonPrefix(a: string, b: string): number {
  let i = 0
  while (i < a.length && i < b.length && a[i] === b[i]) i++
  return i
}

/**
 * One query word against one catalog word. People type the job, the catalog
 * names the field: «ерготерапевт» → «Ерготерапія», «фізичний терапевт» →
 * «Фізична терапія», «реабілітолог» → «реабілітаційна». Ukrainian endings
 * differ, so a long word also matches on a shared stem: all but its last
 * four letters, at least five (2026-10-24: «ерготерапевт» found nothing).
 */
function wordHit(q: string, w: string): 'prefix' | 'stem' | null {
  if (w.startsWith(q)) return 'prefix'
  if (q.length >= 6 && commonPrefix(q, w) >= Math.max(5, q.length - 4)) return 'stem'
  return null
}

/** How every query word fares against the words of a name: the weakest hit counts. */
function wordsHit(qWords: string[], hay: string[]): 'prefix' | 'stem' | null {
  let weakest: 'prefix' | 'stem' = 'prefix'
  for (const q of qWords) {
    let best: 'prefix' | 'stem' | null = null
    for (const w of hay) {
      const hit = wordHit(q, w)
      if (hit === 'prefix') { best = 'prefix'; break }
      if (hit) best = hit
    }
    if (!best) return null
    if (best === 'stem') weakest = 'stem'
  }
  return weakest
}

export function searchSpecialties(rows: SpecialtyOption[], query: string, language: string, exclude: string[] = []): SpecialtyOption[] {
  const ex = new Set(exclude)
  const base = rows.filter(r => !ex.has(r.id))
  const q = norm(query)
  if (!q) return base
  const qWords = words(q)
  const scored = base.map(r => {
    const names = [r.nameUk, r.nameEn, ...r.aliases].map(norm)
    const nameWords = names.flatMap(words)
    const label = norm(specialtyLabel(r, language))
    // Group label matches only count as a whole-word hit (e.g. the query equals
    // "медсестри" from "Медсестри та акушерки"). A loose substring match against the
    // label would match every item sharing a group on any prefix of any label word
    // (e.g. "медсес" is a prefix of "медсестри"), pulling in unrelated specialties.
    const groupWords = words(groupLabel(r.groupKey, language))
    const hit = qWords.length > 0 ? wordsHit(qWords, nameWords) : null
    let score = 0
    if (label.startsWith(q)) score = 4
    else if (names.some(h => h.startsWith(q)) || hit === 'prefix') score = 3
    else if (hit === 'stem' || (qWords.length > 0 && qWords.every(w => groupWords.includes(w)))) score = 2
    else if (names.some(h => h.includes(q))) score = 1
    return { r, score }
  }).filter(x => x.score > 0)
  // Tiebreak by original catalog position (the API returns rows in catalog order);
  // `sortOrderIndex` does not exist on SpecialtyOption.
  scored.sort((a, b) => b.score - a.score || base.indexOf(a.r) - base.indexOf(b.r))
  return scored.map(x => x.r)
}

export function groupSpecialties(rows: SpecialtyOption[], language: string) {
  const out: Array<{ key: GroupKey; label: string; items: SpecialtyOption[] }> = []
  for (const key of GROUP_ORDER) {
    const items = rows.filter(r => r.groupKey === key)
    if (items.length) out.push({ key, label: groupLabel(key, language), items })
  }
  return out
}
